import { useState, useEffect } from 'react';
import { BrandNav, SiteFooter } from '../components/SiteChrome';
import { BarChart3, Globe, Layers, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className="w-3.5 h-3.5 text-brand" />}
        <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-600">{label}</span>
      </div>
      <div className="text-2xl font-bold font-mono text-brand">{value}</div>
    </div>
  );
}

function DistributionBar({ label, pct }) {
  return (
    <div>
      <div className="flex justify-between text-xs text-zinc-400 mb-1">
        <span>{label}</span>
        <span className="font-mono">{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-zinc-800">
        <div
          className="h-1.5 rounded-full bg-brand transition-all duration-500"
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
    </div>
  );
}

export default function UsageInsights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/public/usage-insights`)
      .then((r) => r.json())
      .then((json) => setData(json))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-black flex flex-col">
      <BrandNav width="max-w-4xl" />

      <main className="flex-1 max-w-4xl mx-auto px-4 py-8 w-full">
        <div className="mb-8">
          <h1 className="text-2xl font-bold tracking-tight mb-2" data-testid="usage-insights-title">
            Platform Usage Insights
          </h1>
          <p className="text-sm text-zinc-500">
            This page shows aggregated platform usage statistics to improve product quality and transparency.
            It does not display personal information, trading activity, or security recommendations.
          </p>
        </div>

        {loading && (
          <div className="text-sm text-zinc-500 py-12 text-center">Loading insights...</div>
        )}

        {!loading && !data && (
          <div className="text-sm text-zinc-500 py-12 text-center">No usage data available yet.</div>
        )}

        {!loading && data && (
          <div className="space-y-6" data-testid="insights-content">
            {/* Top Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <StatCard
                label="Total Analyses"
                value={data.total_analyses?.toLocaleString() || '0'}
                icon={BarChart3}
              />
              <StatCard
                label="Period"
                value={data.period?.replace(/_/g, ' ') || 'N/A'}
                icon={Clock}
              />
            </div>

            {/* Market Distribution */}
            {data.market_distribution && data.market_distribution.length > 0 && (
              <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-5" data-testid="market-distribution">
                <div className="flex items-center gap-2 mb-4">
                  <Globe className="w-4 h-4 text-brand" />
                  <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">Market Distribution</span>
                </div>
                <div className="space-y-3">
                  {data.market_distribution.map((row) => (
                    <DistributionBar key={row.market} label={row.market} pct={row.share_pct} />
                  ))}
                </div>
              </div>
            )}

            {/* Sector Distribution */}
            {data.sector_distribution && data.sector_distribution.length > 0 && (
              <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-5" data-testid="sector-distribution">
                <div className="flex items-center gap-2 mb-4">
                  <Layers className="w-4 h-4 text-brand" />
                  <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">Sector Distribution</span>
                </div>
                <div className="space-y-3">
                  {data.sector_distribution.map((row) => (
                    <DistributionBar key={row.sector} label={row.sector} pct={row.share_pct} />
                  ))}
                </div>
              </div>
            )}

            {/* Usage Time Bands */}
            {data.usage_time_bands && data.usage_time_bands.length > 0 && (
              <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-5" data-testid="time-bands">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-4 h-4 text-brand" />
                  <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">Usage Time Bands</span>
                </div>
                <div className="space-y-3">
                  {data.usage_time_bands.map((row) => (
                    <DistributionBar key={row.band} label={row.band} pct={row.share_pct} />
                  ))}
                </div>
              </div>
            )}

            {/* Disclaimer */}
            <div className="bg-zinc-900/30 border border-zinc-800/50 rounded-lg p-4">
              <p className="text-xs text-zinc-600 text-center">
                {data.disclaimer || 'Aggregated platform usage data only. No financial advice or recommendations.'}
              </p>
            </div>
          </div>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}
